
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Loader2, Users, Check } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useToast } from '@/components/ui/use-toast';
import { Segment } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { applyTenantOverrides } from '@/components/utils/tenantOverrides';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { buildTechStackCategories, applyRules } from '@/lib/segmentApplyRules';
import { useTranslation } from '@/components/utils/translations';
import {
  translateIncumbentSentiment,
  translateProcurementFriendliness,
} from '@/lib/accountClassificationLabels';

const MultiSelect = ({ options, value, onChange, placeholder = "Select options", formatOption }) => {
    const [open, setOpen] = useState(false);
    const selectedValues = new Set(Array.isArray(value) ? value : []);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" className="w-48 justify-start font-normal">
                    {(Array.isArray(value) ? value.length : 0) > 0 ? `${Array.isArray(value) ? value.length : 0} selected` : placeholder}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0" align="start">
                <Command>
                    <CommandInput placeholder="Filter..." />
                    <CommandList>
                        <CommandEmpty>No results found.</CommandEmpty>
                        <CommandGroup>
                            {options.map(option => {
                                const isSelected = selectedValues.has(option);
                                return (
                                    <CommandItem key={option} onSelect={() => {
                                        isSelected ? selectedValues.delete(option) : selectedValues.add(option);
                                        onChange(Array.from(selectedValues));
                                    }}>
                                        <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary ${isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"}`}>
                                           <Check className="h-4 w-4" />
                                        </div>
                                        <span>{formatOption ? formatOption(option) : option}</span>
                                    </CommandItem>
                                )
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default function SegmentBuilder({ currentTenant: currentTenantProp }) {
    const contextUser = useUser();
    const contextTenant = useCurrentTenant();
    const currentTenant = currentTenantProp || contextTenant;
    const [segmentId, setSegmentId] = useState(null);
    const [segmentTenantId, setSegmentTenantId] = useState(null);
    const [segmentName, setSegmentName] = useState('');
    const [rules, setRules] = useState([{ field: 'account_fit_score', operator: '>=', value: '80' }]);
    const [allAccounts, setAllAccounts] = useState([]);
    const [filteredCount, setFilteredCount] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { toast } = useToast();
    const { t, language } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const availableCountries = useMemo(() => {
        const countries = new Set();
        allAccounts.forEach(acc => {
            if (acc.country) countries.add(acc.country);
        });
        return Array.from(countries).sort();
    }, [allAccounts]);

    const techStackCategories = useMemo(() => buildTechStackCategories(allAccounts), [allAccounts]);

    const criteriaOptions = useMemo(() => {
        const base = [
            { value: 'account_fit_score', label: 'Account Fit Score', type: 'number' },
            { value: 'country', label: 'Country', type: 'multi-select', options: availableCountries },
            { value: 'enrolment', label: 'Enrolment', type: 'number' },
            { value: 'decision_window_band', label: 'Decision Window', type: 'enum', options: ['<12 months', '12-24 months', '>24 months', 'N/A'] },
            { value: 'procurement_friendliness', label: t('procurementFriendliness'), type: 'enum', options: ['Friendly', 'Standard', 'Bureaucratic', 'Monitor'], formatOption: (opt) => translateProcurementFriendliness(language, opt) },
            {
                value: 'incumbent_sentiment',
                label: t('incumbentSentiment'),
                type: 'multi-select',
                options: ['Positive', 'Neutral', 'Negative'],
                formatOption: (opt) => translateIncumbentSentiment(language, opt),
            },
        ];
        if (Object.keys(techStackCategories).length > 0) {
            base.push({ value: 'tech_stack', label: 'Tech Stack', type: 'tech-stack' });
        }
        return base;
    }, [availableCountries, techStackCategories, t, language]);

    useEffect(() => {
        const fetchInitialData = async () => {
            if (!contextUser) return;
            setIsLoading(true);

            const user = contextUser;
            const params = new URLSearchParams(location.search);
            const id = params.get('id');

            let existingSegment = null;
            if (id) {
                existingSegment = await Segment.get(id);
                console.log('📂 BUILDER: Loaded existing segment:', {
                    id: existingSegment.id,
                    name: existingSegment.name,
                    tenant_id: existingSegment.tenant_id,
                    user_id: existingSegment.user_id,
                    rules: existingSegment.rules
                });
                // Check user ownership - only allow editing own segments
                if (existingSegment.user_id && existingSegment.user_id !== user?.id) {
                   toast({ 
                         title: "Access Denied", 
                         description: "You don't have permission to edit this segment.", 
                        variant: "destructive" 
                    });
                    navigate(createPageUrl('Segments'));
                    setIsLoading(false);
                    return;
                }
            }
            
            // FIX: Prioritize admin-active-tenant for admins
            const tenantId = existingSegment?.tenant_id ||
                resolveEffectiveTenant(
                    user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
                    currentTenant,
                    user,
                    'instructure'
                );

            console.log('🏢 BUILDER: Tenant context:', {
                adminTenant: user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
                userRole: user?.role,
                existingSegmentTenant: existingSegment?.tenant_id,
                segmentTenantId,
                currentTenant,
                userTenant: user?.tenant_id,
                finalTenantId: tenantId
            });

            setSegmentTenantId(tenantId);

            let tenantFilter;
if (tenantId === 'instructure') {
    tenantFilter = { $in: [tenantId, 'clairelabs', 'default', 'global'] };
} else if (tenantId === 'smowl') {
    tenantFilter = { $in: [tenantId] };
} else if (tenantId === 'diambra') {
    tenantFilter = { $in: [tenantId, 'smowl', 'instructure', 'clairelabs', 'base44', 'default', 'global'] };
} else if (tenantId === 'clairelabs') {
    tenantFilter = { $in: [tenantId, 'default', 'global'] };
} else if (tenantId === 'edqwest' || tenantId === 'faria') {
    tenantFilter = { $in: ['edqwest', 'faria', 'global'] };
} else if (tenantId === 'calmedica') {
    tenantFilter = { $in: [tenantId, 'global'] };
} else if (tenantId === 'kairadigital') {
    tenantFilter = { $in: [tenantId, 'global'] };
} else if (tenantId === 'demarque' || tenantId === 'demarque_V2' || tenantId === 'demarque_v2') {
    tenantFilter = { $in: [tenantId, 'global'] };
} else if (tenantId === 'prowise') {
    tenantFilter = { $in: [tenantId, 'global'] };
} else if (tenantId === 'skillwell') {
    tenantFilter = { $in: [tenantId, 'global'] };
} else if (tenantId === 'sebit') {
    tenantFilter = { $in: [tenantId, 'global'] };
} else if (tenantId === 'plataformaa') {
    tenantFilter = { $in: [tenantId, 'global'] };
} else if (tenantId === 'wayground') {
    tenantFilter = { $in: [tenantId, 'plataformaa', 'global'] }; // wayground can see plataformaa data
} else {
    // Default fallback for any new tenant
    tenantFilter = { $in: [tenantId, 'global'] };
}

            const allAccountsRaw = await AccountFinal.filter({ tenant_id: tenantFilter });

            const accountsWithOverrides = allAccountsRaw
                .map(acc => applyTenantOverrides(acc, tenantId))
                .filter(acc => acc.is_visible !== false);

            const allowedCountries = {
                instructure: ['Australia', 'United Kingdom','Hong Kong'],
                clairelabs: ['Australia', 'Hong Kong'],
                smowl: ['Hong Kong', 'South Africa', 'Australia', 'United Kingdom', 'Kenya'],
                diambra: ['United Kingdom'],
                edqwest: ['France', 'UAE', 'United Kingdom'],
                faria: ['France', 'United Arab Emirates', 'Spain','Italy','United Kingdom','Luxembourg'],
                calmedica: ['France'],
                kairadigital: ['France','United Kingdom','Sweden','Switzerland','Germany','Netherlands'],
                demarque: ['Canada','Morocco','Germany','Tunisia','USA','Kenya','South Africa'],
                demarque_V2: ['Canada','Morocco','Germany','Tunisia','USA','Kenya','South Africa'],
                demarque_v2: ['Canada','Morocco','Germany','Tunisia','USA','Kenya','South Africa'],
                prowise: ['Belgium','Spain','Netherlands','Poland'],
                skillwell: ['USA'],
                sebit: ['France','United Kingdom','Switzerland','Germany','Netherlands','United Arab Emirates'],
                plataformaa: ['Chile','Colombia','Mexico','Peru'],
                wayground: ['Chile','Colombia','Mexico','Peru','Argentina','Uruguay'],
            };

            const hongKongAccountsToExclude = [
                'City University of Hong Kong (CityU)',
                'The Hong Kong University of Science and Technology (HKUST)',
            ];

            const filteredAccounts = accountsWithOverrides.filter(acc => {
                if (tenantId === 'instructure' && hongKongAccountsToExclude.includes(acc.account_name)) {
                    return false;
                }

                const allowed = allowedCountries[tenantId];
                if (allowed && acc.country) {
                    return allowed.includes(acc.country);
                }

                return true;
            });

            console.log('📊 BUILDER loaded accounts:', {
                tenantId,
                allowedCountries: allowedCountries[tenantId],
                total: allAccountsRaw.length,
                afterOverrides: accountsWithOverrides.length,
                afterFiltering: filteredAccounts.length,
                countries: [...new Set(filteredAccounts.map(a => a.country))],
                accountIds: filteredAccounts.map(a => a.id)
            });

            setAllAccounts(filteredAccounts);

            if (id && existingSegment) {
                setSegmentId(id);
                setSegmentName(existingSegment.name);
                const parsedRules = (existingSegment.rules || []).map(rule => ({
                    ...rule,
                    value:
                        rule.value &&
                        typeof rule.value === 'string' &&
                        rule.value.startsWith('[')
                            ? JSON.parse(rule.value)
                            : rule.value,
                }));
                console.log('📋 BUILDER: Parsed rules:', parsedRules);
                setRules(parsedRules);
            }

            setIsLoading(false);
        };

        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, currentTenant, contextUser]);

    useEffect(() => {
        const matched = applyRules(allAccounts, rules, techStackCategories);
        setFilteredCount(matched.length);
    }, [rules, allAccounts, techStackCategories]);

    const addRule = () => setRules([...rules, { field: 'country', operator: 'in', value: [] }]);

    const updateRule = (index, part, value) => {
        const newRules = [...rules];
        const oldField = newRules[index].field;
        newRules[index][part] = value;
        if (part === 'field') {
            const newOption = criteriaOptions.find(o => o.value === value);
            const oldOption = criteriaOptions.find(o => o.value === oldField);
            if (newOption.type !== oldOption?.type) {
                if (newOption.type === 'tech-stack') {
                    newRules[index].operator = 'in';
                    newRules[index].category = Object.keys(techStackCategories)[0] || null;
                    newRules[index].value = [];
                } else if (newOption.type === 'number') {
                    newRules[index].operator = '>=';
                    newRules[index].value = '80';
                    delete newRules[index].category;
                } else if (newOption.type === 'multi-select') {
                    newRules[index].operator = 'in';
                    newRules[index].value = [];
                    delete newRules[index].category;
                } else { // enum type
                    newRules[index].operator = '==';
                    newRules[index].value = newOption.options[0];
                    delete newRules[index].category;
                }
            }
            // If field changes TO tech-stack, ensure category is set
            if (newOption.type === 'tech-stack' && !newRules[index].category) {
                newRules[index].category = Object.keys(techStackCategories)[0] || null;
            }
            // If field changes FROM tech-stack, clear category
            if (oldOption?.type === 'tech-stack' && newOption.type !== 'tech-stack') {
                delete newRules[index].category;
            }
        }
        if (part === 'category') {
            // When category changes, reset value to empty array for multi-select
            newRules[index].value = [];
        }
        console.log('🔄 BUILDER: Rule updated:', { index, part, value, newRule: newRules[index] });
        setRules(newRules);
    };

    const removeRule = (index) => setRules(rules.filter((_, i) => i !== index));

    const handleSave = async () => {
        if (!segmentName) {
            toast({ title: "Error", description: "Please enter a name.", variant: "destructive" });
            return;
        }
        setIsSaving(true);
        try {
            const rulesForStorage = rules.map(rule => ({
                ...rule,
                value: Array.isArray(rule.value) ? JSON.stringify(rule.value) : rule.value,
            }));
    
            const user = contextUser;
            if (!user) {
                toast({ title: "Error", description: "Please sign in to save.", variant: "destructive" });
                setIsSaving(false);
                return;
            }
    
            const resolvedTenant =
                segmentTenantId ||
                resolveEffectiveTenant(
                    user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
                    currentTenant,
                    user,
                    'instructure'
                );
    
            if (!segmentTenantId) {
                setSegmentTenantId(resolvedTenant);
            }
    
            const payload = {
                name: segmentName,
                rules: rulesForStorage,
                tenant_id: resolvedTenant,
                user_id: user?.id,  // ADD THIS - user isolation
            };
    
            console.log('💾 BUILDER: Saving segment:', payload);
    
            const savedSegment = segmentId
                ? await Segment.update(segmentId, payload)
                : await Segment.create(payload);
    
            console.log('✅ BUILDER: Segment saved:', savedSegment);
    
            toast({ title: `Segment ${segmentId ? 'Updated' : 'Saved'}!` });
            navigate(createPageUrl(`SegmentResults?id=${savedSegment.id}&tenant_id=${savedSegment.tenant_id || resolvedTenant}`));
        } catch (error) {
            console.error('❌ BUILDER: Save error:', error);
            toast({ title: "Error", description: "Failed to save segment.", variant: "destructive" });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div className="text-center p-20"><Loader2 className="w-8 h-8 animate-spin mx-auto"/></div>
    }

    return (
        <div className="max-w-4xl mx-auto px-6 py-8">
            <Link to={createPageUrl('Segments')} className="flex items-center gap-2 text-sm text-[#5C6B7A] hover:text-[#0A1A2F] mb-4">
                <ArrowLeft className="w-4 h-4" /> Back to Segments
            </Link>
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold text-[#0A1A2F]">{segmentId ? 'Edit Segment' : 'Segment Builder'}</h1>
                <Button onClick={handleSave} className="bg-[#00C2C2] hover:bg-[#00A3A3]" disabled={isSaving}>
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {segmentId ? 'Update Segment' : 'Save Segment'}
                </Button>
            </div>

            <div className="p-6 border rounded-lg shadow-md bg-white space-y-6">
                <div>
                    <label className="font-medium text-sm">Segment Name</label>
                    <Input value={segmentName} onChange={e => setSegmentName(e.target.value)} placeholder="e.g., High-Priority Canadian Universities" />
                </div>
                
                <div className="space-y-4">
                    <label className="font-medium text-sm">Rules</label>
                    {rules.map((rule, index) => {
                        const option = criteriaOptions.find(o => o.value === rule.field);
                        const isTechStack = option?.type === 'tech-stack';
                        const categoryConfig = isTechStack && rule.category ? techStackCategories[rule.category] : null;
                        
                        return (
                            <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-md flex-wrap">
                                <Select value={rule.field} onValueChange={v => updateRule(index, 'field', v)}>
                                    <SelectTrigger className="w-48">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {criteriaOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                
                                {isTechStack && (
                                    <>
                                        <Select value={techStackCategories[rule.category] ? rule.category : (Object.keys(techStackCategories)[0] || '')} onValueChange={v => updateRule(index, 'category', v)}>
                                            <SelectTrigger className="w-40">
                                                <SelectValue placeholder="Category" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.keys(techStackCategories).map(cat => (
                                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <div className="w-16 px-3 py-2 text-sm text-center text-gray-500">is</div>
                                        <MultiSelect 
                                            options={categoryConfig?.vendors || []} 
                                            value={rule.value || []} 
                                            onChange={v => updateRule(index, 'value', v)} 
                                            placeholder="Select vendors"
                                        />
                                    </>
                                )}
                                
                                {option?.type === 'number' && (
                                    <>
                                        <Select value={rule.operator} onValueChange={v => updateRule(index, 'operator', v)}>
                                            <SelectTrigger className="w-24">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value=">=">≥</SelectItem>
                                                <SelectItem value="<=">≤</SelectItem>
                                                <SelectItem value="==">=</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Input type="number" value={rule.value} onChange={e => updateRule(index, 'value', e.target.value)} className="w-48" />
                                    </>
                                )}
                                
                                {option?.type === 'enum' && (
                                    <>
                                        <div className="w-24 px-3 py-2 text-sm text-center text-gray-500">is</div>
                                        <Select value={rule.value} onValueChange={v => updateRule(index, 'value', v)}>
                                            <SelectTrigger className="w-48">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {option.options.map(opt => (
                                                    <SelectItem key={opt} value={opt}>
                                                        {option.formatOption ? option.formatOption(opt) : opt}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </>
                                )}
                                
                                {option?.type === 'multi-select' && (
                                    <>
                                        <div className="w-24 px-3 py-2 text-sm text-center text-gray-500">is in</div>
                                        <MultiSelect
                                            options={option.options}
                                            value={rule.value || []}
                                            onChange={v => updateRule(index, 'value', v)}
                                            formatOption={option.formatOption}
                                        />
                                    </>
                                )}
                                
                                {rules.length > 1 && (
                                    <Button variant="ghost" size="icon" onClick={() => removeRule(index)}>
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                )}
                            </div>
                        );
                    })}
                </div>
                <Button variant="outline" onClick={addRule}><Plus className="w-4 h-4 mr-2" /> Add Rule</Button>
            </div>
            
            <div className="p-6 mt-6 border rounded-lg shadow-md bg-[#0A1A2F] text-white">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-[#00C2C2]/20 rounded-lg"><Users className="w-6 h-6 text-[#00C2C2]"/></div>
                    <div>
                        <h3 className="font-semibold text-lg">Live Preview</h3>
                        <p className="text-3xl font-bold">{filteredCount}</p>
                        <p className="text-sm text-gray-300">accounts match your criteria</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
